import { registerPlugin } from "@capacitor/core";

/**
 * The semantic names the native side maps to `HapticFeedbackConstants`. Kept
 * as one flat set rather than two families because Android has a single
 * constant vocabulary — the impact/notification split is an iOS distinction.
 */
export type VidimoHapticKind =
  | "success"
  | "warning"
  | "error"
  | "light"
  | "medium"
  | "heavy";

interface VidimoHapticsPlugin {
  perform: (options: { kind: VidimoHapticKind }) => Promise<void>;
}

/**
 * Android-only bridge to `View.performHapticFeedback`
 * (`apps/mobile/android/app/src/main/java/world/superhost/app/VidimoHapticsPlugin.java`).
 *
 * `@capacitor/haptics` drives Android with raw `Vibrator` waveforms that reach
 * the system as `USAGE_UNKNOWN`, so Android's own "Touch feedback" setting
 * does not silence them, and its hardcoded patterns are wrong besides (its
 * LIGHT impact lasts longer than its MEDIUM; its WARNING is one unbroken 210ms
 * buzz). The platform constants are per-device tuned and honour that setting,
 * which is why Android routes here while iOS stays on `@capacitor/haptics` and
 * its correct UIKit generators. See the Java class for the full account.
 *
 * There is no web implementation on purpose: the caller
 * ([`lib/haptics.ts`](../haptics.ts)) only reaches this on native Android.
 */
export const VidimoHaptics =
  registerPlugin<VidimoHapticsPlugin>("VidimoHaptics");
